import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  DollarSign,
  CheckCircle2,
  XCircle,
  Clock,
  CreditCard,
  Banknote,
  FileText,
  ArrowRight,
  Receipt,
} from 'lucide-react';

import { formatDateTime } from '../lib/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatLKR = (n) =>
  `LKR ${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_STYLES = {
  approved: { bg: 'bg-emerald-100', text: 'text-emerald-800', icon: CheckCircle2, label: 'Approved' },
  pending:  { bg: 'bg-amber-100',   text: 'text-amber-800',   icon: Clock,        label: 'Pending review' },
  rejected: { bg: 'bg-red-100',     text: 'text-red-800',     icon: XCircle,      label: 'Rejected' },
};

const METHOD_META = {
  bank_transfer: { label: 'Bank transfer', icon: Banknote },
  cash:          { label: 'Cash',          icon: Banknote },
  stripe:        { label: 'Online (Stripe)', icon: CreditCard },
  other:         { label: 'Other',         icon: DollarSign },
};

/**
 * Resident-facing payment history. Replaces the legacy "type any amount and
 * pay Stripe" page (which never linked payments to invoices) with a proper
 * chronological ledger of every payment the resident has made —
 * bank-transfer submissions, cash logs, and Stripe payments — including
 * which invoices each payment settled.
 */
export const Payments = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');   // all | approved | pending | rejected

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/payments/history`, { withCredentials: true });
      setPayments(data.payments || []);
    } catch (err) {
      console.error('Payments: failed to load history', err);
      toast.error(err.response?.data?.detail || 'Failed to load payment history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === 'all'
    ? payments
    : payments.filter((p) => p.status === filter);

  const totalApproved = payments
    .filter((p) => p.status === 'approved')
    .reduce((s, p) => s + Number(p.amount || 0), 0);
  const totalPending = payments
    .filter((p) => p.status === 'pending')
    .reduce((s, p) => s + Number(p.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="payments-history-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>Payment History</h1>
          <p className="text-sm text-[#64748b]">
            Every payment you've made — bank transfers, cash logs, and online (Stripe) — and which invoices each one settled.
          </p>
        </div>
        <Button
          onClick={() => navigate('/invoices')}
          className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
          data-testid="go-to-invoices-button"
        >
          <Receipt className="mr-2 h-4 w-4" /> View / Pay Invoices <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>

      {/* Summary chips */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="p-4 bg-white border border-[#e2e8f0]" data-testid="summary-total-paid">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Total paid</p>
          <p className="text-2xl font-semibold mt-1 text-[#10b981]" style={{ fontFamily: 'Outfit' }}>{formatLKR(totalApproved)}</p>
          <p className="text-xs text-[#64748b] mt-1">Across all approved payments</p>
        </Card>
        <Card className="p-4 bg-white border border-[#e2e8f0]" data-testid="summary-pending">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Awaiting approval</p>
          <p className="text-2xl font-semibold mt-1 text-amber-700" style={{ fontFamily: 'Outfit' }}>{formatLKR(totalPending)}</p>
          <p className="text-xs text-[#64748b] mt-1">Bank slips waiting for management review</p>
        </Card>
        <Card className="p-4 bg-white border border-[#e2e8f0]" data-testid="summary-count">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Payments logged</p>
          <p className="text-2xl font-semibold mt-1 text-[#0f172a]" style={{ fontFamily: 'Outfit' }}>{payments.length}</p>
          <p className="text-xs text-[#64748b] mt-1">Newest first</p>
        </Card>
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap gap-2" data-testid="payments-filters">
        {[
          { id: 'all',      label: 'All',      count: payments.length },
          { id: 'approved', label: 'Approved', count: payments.filter(p => p.status === 'approved').length },
          { id: 'pending',  label: 'Pending',  count: payments.filter(p => p.status === 'pending').length },
          { id: 'rejected', label: 'Rejected', count: payments.filter(p => p.status === 'rejected').length },
        ].map((f) => {
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                active
                  ? 'bg-[#0f172a] text-white'
                  : 'bg-[#f1f5f9] text-[#475569] hover:bg-[#e2e8f0]'
              }`}
              data-testid={`payments-filter-${f.id}`}
            >
              {f.label} ({f.count})
            </button>
          );
        })}
      </div>

      {/* List */}
      {loading ? (
        <Card className="p-12 text-center bg-white border border-[#e2e8f0]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563eb] mx-auto mb-3" />
          <p className="text-sm text-[#64748b]">Loading your payment history…</p>
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center bg-white border border-[#e2e8f0]" data-testid="payments-empty">
          <DollarSign className="h-10 w-10 text-[#cbd5e1] mx-auto mb-3" />
          <p className="text-sm font-semibold" style={{ fontFamily: 'Outfit' }}>
            {filter === 'all' ? 'No payments logged yet.' : `No ${filter} payments.`}
          </p>
          <p className="text-xs text-[#64748b] mt-1">
            {filter === 'all'
              ? 'When you pay an invoice online or log a bank transfer, it will appear here.'
              : 'Switch the filter to "All" to see everything.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((p) => (
            <PaymentRow key={p.id} p={p} />
          ))}
        </div>
      )}
    </div>
  );
};

const PaymentRow = ({ p }) => {
  const style = STATUS_STYLES[p.status] || STATUS_STYLES.pending;
  const StatusIcon = style.icon;
  const method = METHOD_META[p.method] || METHOD_META.other;
  const MethodIcon = method.icon;
  return (
    <Card className="p-4 bg-white border border-[#e2e8f0]" data-testid={`payment-row-${p.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`h-10 w-10 rounded-md ${style.bg} flex items-center justify-center flex-shrink-0`}>
            <MethodIcon className={`h-5 w-5 ${style.text}`} />
          </div>
          <div className="min-w-0">
            <p className="text-lg font-semibold text-[#0f172a]" style={{ fontFamily: 'Outfit' }}>
              {formatLKR(p.amount)}
            </p>
            <p className="text-xs text-[#64748b]">
              {method.label}
              {p.date && <> · {formatDateTime(p.date)}</>}
              {p.kind === 'manual' && p.submitted_at && p.date !== p.submitted_at && (
                <> · submitted {formatDateTime(p.submitted_at)}</>
              )}
            </p>
          </div>
        </div>
        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full inline-flex items-center gap-1 ${style.bg} ${style.text}`}>
          <StatusIcon className="h-3 w-3" /> {style.label}
        </span>
      </div>

      {p.note && (
        <p className="text-xs text-[#334155] mt-3 pl-13">
          <span className="text-[#64748b]">Note:</span> <em>"{p.note}"</em>
        </p>
      )}

      {p.status === 'rejected' && p.rejection_reason && (
        <p className="text-xs text-red-800 bg-red-50 border border-red-200 rounded p-2 mt-3">
          <strong>Rejection reason:</strong> {p.rejection_reason}
        </p>
      )}

      {p.allocations && p.allocations.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#f1f5f9]">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-[#64748b] mb-1">
            Settled {p.allocations.length} invoice{p.allocations.length === 1 ? '' : 's'}
          </p>
          <ul className="text-xs space-y-0.5">
            {p.allocations.map((a) => (
              <li key={a.invoice_id} className="flex items-center justify-between gap-2">
                <span className="text-[#334155]">
                  {a.invoice_type ? a.invoice_type.replace('_', ' ') : 'Invoice'}
                  {a.period && <> · {a.period}</>}
                </span>
                <span className="text-[#0f172a] font-medium tabular-nums">{formatLKR(a.applied)}</span>
              </li>
            ))}
            {p.surplus > 0 && (
              <li className="flex items-center justify-between gap-2 pt-1 mt-1 border-t border-dashed border-[#e2e8f0]">
                <span className="text-emerald-700 font-medium">Credit balance (banked)</span>
                <span className="text-emerald-700 font-semibold tabular-nums">{formatLKR(p.surplus)}</span>
              </li>
            )}
          </ul>
        </div>
      )}

      {p.slip_path && (
        <p className="text-xs mt-2">
          <a
            href={`${API}/files/${p.slip_path}?auth=${encodeURIComponent(document.cookie.match(/access_token=([^;]+)/)?.[1] || '')}`}
            target="_blank" rel="noreferrer"
            className="text-blue-600 hover:underline inline-flex items-center gap-1"
            data-testid={`view-slip-${p.id}`}
          >
            <FileText className="h-3 w-3" /> View bank slip
          </a>
        </p>
      )}
    </Card>
  );
};

// ---------- Stripe success / cancel screens (unchanged behaviour) ----------
export const PaymentSuccess = () => {
  const navigate = useNavigate();
  return (
    <div className="max-w-md mx-auto py-16 text-center" data-testid="payment-success-page">
      <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
        <CheckCircle2 className="h-8 w-8 text-emerald-600" />
      </div>
      <h1 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>Payment successful</h1>
      <p className="text-sm text-[#64748b] mb-6">Your invoices are being marked paid. It may take a few seconds.</p>
      <div className="flex justify-center gap-2">
        <Link to="/payments">
          <Button className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white">View Payment History</Button>
        </Link>
        <Link to="/invoices">
          <Button variant="outline" className="border-[#e2e8f0]">Back to Invoices</Button>
        </Link>
      </div>
    </div>
  );
};

export const PaymentCancel = () => (
  <div className="max-w-md mx-auto py-16 text-center" data-testid="payment-cancel-page">
    <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
      <XCircle className="h-8 w-8 text-red-600" />
    </div>
    <h1 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>Payment cancelled</h1>
    <p className="text-sm text-[#64748b] mb-6">No charge was made. You can try again anytime.</p>
    <Link to="/invoices">
      <Button className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white">Back to Invoices</Button>
    </Link>
  </div>
);

export default Payments;
